import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Icon, I } from '../components/ui/icons'
import { gridColumns, mobileButtonStyle, TEXT_WRAP_STYLE } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'

function ImportCard({
  iconBg, iconChar, icon, title, body, cta, recommended, recommendedLabel, disabled, onClick, isMobile = false,
}: {
  iconBg: string
  iconChar?: string
  icon?: ReactNode
  title: string
  body: string
  cta: string
  recommended?: boolean
  recommendedLabel?: string
  disabled?: boolean
  onClick?: () => void
  isMobile?: boolean
}) {
  return (
    <Card padding={22} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 12, borderColor: recommended ? 'var(--brand-300)' : 'var(--border-subtle)' }}>
      {recommended && (
        <div style={{ position: 'absolute', top: -10, insetInlineStart: 16, padding: '3px 9px', background: 'var(--brand-500)', color: 'white', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{recommendedLabel}</div>
      )}
      <div style={{ width: 40, height: 40, borderRadius: 10, background: iconBg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, fontFamily: 'Nunito, sans-serif' }}>
        {iconChar ? iconChar : <Icon size={18}>{icon}</Icon>}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--brand-900)', ...TEXT_WRAP_STYLE }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5, flex: 1, ...TEXT_WRAP_STYLE }}>{body}</div>
      <Button kind={recommended ? 'primary' : 'secondary'} size="sm" disabled={disabled} onClick={onClick} style={isMobile ? mobileButtonStyle(isMobile) : { alignSelf: 'flex-start' }}>{cta}</Button>
    </Card>
  )
}

interface ProductsEmptyPageProps {
  onManualCreate?: () => void
}

export function ProductsEmptyPage({ onManualCreate }: ProductsEmptyPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  return (
    <div style={{ maxWidth: 880, margin: isMobile ? '18px auto 0' : '30px auto 0' }}>
      <Card padding={isMobile ? 28 : 48} style={{ textAlign: 'center', background: 'linear-gradient(180deg, var(--brand-100) 0%, white 100%)' }}>
        <div style={{ width: 64, height: 64, borderRadius: 16, background: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-700)', boxShadow: '0 6px 20px rgba(37,87,232,0.15)' }}>
          <Icon size={28}>{I.package}</Icon>
        </div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em', margin: '20px 0 8px' }}>{t('pages.productsEmpty.title')}</h2>
        <p style={{ fontSize: 14, color: 'var(--fg-muted)', margin: 0, maxWidth: 480, marginInline: 'auto', lineHeight: 1.55 }}>
          {t('pages.productsEmpty.body')}
        </p>
      </Card>

      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', margin: '32px 0 12px' }}>{t('pages.productsEmpty.startPrompt')}</div>

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(3, minmax(0, 1fr))'), gap: 12 }}>
        <ImportCard
          recommended
          disabled
          recommendedLabel={t('pages.productsEmpty.recommended')}
          iconBg="linear-gradient(135deg, var(--brand-500), var(--brand-700))"
          iconChar="Z"
          title={t('pages.productsEmpty.zoho.title')}
          body={t('pages.productsEmpty.zoho.body')}
          cta={t('pages.productsEmpty.comingSoon')}
          isMobile={isMobile}
        />
        <ImportCard
          disabled
          icon={I.globe}
          iconBg="linear-gradient(135deg, var(--brand-500), var(--brand-700))"
          title={t('pages.productsEmpty.channel.title')}
          body={t('pages.productsEmpty.channel.body')}
          cta={t('pages.productsEmpty.comingSoon')}
          isMobile={isMobile}
        />
        <ImportCard
          disabled
          icon={I.download}
          iconBg="linear-gradient(135deg, var(--ink-500), var(--ink-800))"
          title={t('pages.productsEmpty.csv.title')}
          body={t('pages.productsEmpty.csv.body')}
          cta={t('pages.productsEmpty.comingSoon')}
          isMobile={isMobile}
        />
      </div>

      <div style={{ marginTop: 18, padding: '14px 18px', border: '1px dashed var(--border-default)', borderRadius: 12, display: isMobile ? 'grid' : 'flex', alignItems: isMobile ? 'stretch' : 'center', gap: 12, fontSize: 13, minWidth: 0 }}>
        <Icon size={16} color="var(--fg-subtle)">{I.plus}</Icon>
        <span style={{ color: 'var(--fg-muted)' }}>{t('pages.productsEmpty.or')}</span>
        <button onClick={onManualCreate} style={{ color: 'var(--brand-700)', fontWeight: 700, textDecoration: 'none', border: 'none', background: 'transparent', fontFamily: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'start', ...TEXT_WRAP_STYLE }}>{t('pages.productsEmpty.manual')}</button>
      </div>
    </div>
  )
}
